/**
 * What can be associated with THIS record — derived from the metamodel, never
 * hand-wired (#484).
 *
 * The console had two answers to that question and neither was data. The
 * generic route (`/e/$entity/$id`) had none at all: a bare form, a global ⌘K,
 * no way to add a contact to the organization you were looking at. The CRM
 * route had a good one, hardcoded — `sec.related === 'contacts' || 'opportunities'
 * || 'companies'` with hand-written labels and an `entity === 'core_organization'`
 * branch — under a comment admitting that verbs "should later derive from the
 * semantic layer's relationship pairs". This module is that derivation.
 *
 * ── Where the answer already lived ─────────────────────────────────────────
 * `core.describe_schema()` has returned `entities` + `relationships` +
 * `relationship_pairs` since core 0025, and the console fetches it on every
 * page (`useSchema`). The data was already on the client, typed `unknown[]` and
 * unused. Nothing here queries anything new; it reads what was already there.
 *
 * This is deliberately the same shape as `app/federatedSearch.ts`: pure
 * functions over registry rows, unit-tested without a database, so a new
 * module's relationship appears on its records with ZERO console changes.
 *
 * ── The two storages, and why the direction matters so much ────────────────
 * `storage='edge'` — the link is a row in `core.relationship`. Both ends are
 * lists; adding one means inserting an edge.
 *
 * `storage='fk'` — the link is `fk_column` ON `from_type`'s table. The two ends
 * are then NOT symmetric, and conflating them is how a UI offers to "add" a
 * child by overwriting the parent's own column:
 *   · from the FROM end, the record holds the key — one value, set on the
 *     record itself. That is a FIELD, and this module says so (`kind: 'field'`)
 *     rather than pretending it is a list you can append to.
 *   · from the TO end, other rows point AT this record — a genuine list, and
 *     adding one means creating/updating a row of the OTHER entity.
 */
import type { PaletteScope } from '../app/paletteScope'
import { writeDoorOf, type WriteDoor } from '../data/writeState'
import type { EntityTypeInfo, RelationshipAttributeInfo, RelationshipPairInfo, RelationshipTypeInfo, SchemaDescription } from '@solum/sdk'

/** A metamodel-declared fact carried by an edge, in stable display order. */
export interface RelationAttribute {
  key: string
  label: string
  kind: string
  /** `selectable: false` ⇒ an archived vocabulary entry: labelled, not offered (#1075). */
  options: { value: unknown; label: string; selectable?: boolean }[]
}

/** One way this record can be related to others — the unit a surface renders. */
export interface RelationSlot {
  /** stable id for keys/tabs: `<relType>:<direction>:<otherEntity>` */
  id: string
  relType: string
  /** the relationship as read FROM THIS RECORD (inverse_label when inbound) */
  label: string
  /**
   * 'out' ⇒ this record is the relationship's from_type; 'in' ⇒ its to_type.
   *
   * 'both' ⇒ the relationship is SYMMETRIC and self-referential, so the two
   * directions are one reading and the slot spans both ends — see
   * `isSymmetric` below. It is never produced for anything else.
   */
  direction: 'out' | 'in' | 'both'
  /** the entity on THIS end — the one the slots were derived for (an edge write needs it) */
  selfEntity: string
  /** the entity on the other end */
  otherEntity: string
  /** the other entity's registered label (its own key if unregistered) */
  otherLabel: string
  storage: 'edge' | 'fk'
  /** for storage='fk': the column, which always lives on from_type's table */
  fkColumn: string | null
  /**
   * For a DISCRIMINATED fk (#1435): the sibling column that says which target a
   * row points at, and the token that means THIS slot's target. Both null on
   * every monomorphic fk and on every edge, and every read and write below then
   * produces exactly the shape it produced before core 0.249.0.
   */
  discriminatorColumn: string | null
  discriminatorValue: string | null
  cardinality: RelationshipTypeInfo['cardinality']
  /**
   * How this slot behaves:
   *  · 'list'  — a related list this record can be given more of
   *  · 'field' — a single value ON this record (the from-end of an fk)
   */
  kind: 'list' | 'field'
  /** an MDM same_as edge — a real relationship, flagged so a surface can group it */
  isIdentity: boolean
  /** where this relationship came from, for provenance in the UI */
  module: string
  /** facts carried by THIS edge type; empty for a bare connection or an FK */
  attributes: RelationAttribute[]
  /** Server-resolved capabilities; absent only on a pre-0.366 deployment. */
  writeMode?: 'generic' | 'governed'
  writeOperations?: ('create' | 'link' | 'update' | 'end')[]
}

const humanize = (s: string): string => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/**
 * The inbound reading of a relationship.
 *
 * `inverse_label` is registered data (core 2026080214) and is used whenever it
 * is there. The fallback is deliberately mechanical and deliberately NOT clever:
 * humanizing the key gives "Works at" on an organization, which is imperfect but
 * TRUE to what is registered, and visibly a default rather than an invention. A
 * console-side table of better phrasings is exactly what #484 removed.
 */
export function inboundLabel(rt: Pick<RelationshipTypeInfo, 'key' | 'inverse_label'>): string {
  const declared = rt.inverse_label?.trim()
  return declared ? declared : humanize(rt.key)
}

/**
 * Is this relationship SYMMETRIC — the same reading from either end?
 *
 * `partner_of` and `org_same_as` declare `inverse_label` EQUAL to `label`, and
 * core 2026080214 says that is deliberate: *"Where an edge is symmetric the
 * inverse is deliberately the same phrase (partner_of, *_same_as): saying so
 * explicitly is what stops a consumer from inventing 'Partner of by'."* So the
 * equality is a registered statement, not an omission, and it is the signal.
 *
 * `directed` is NOT the signal, tempting as it looks: it is declared in
 * core/0001 with `default true` and read by nothing except the wiki generator
 * and an SDK type. Flipping it would change no behaviour anywhere.
 *
 * Symmetry only collapses the two readings when the relationship is ALSO
 * self-referential. `works_at` seen from a person and from an organization is
 * two readings of one edge between two different entities, and must stay two
 * slots even if someone gives it a symmetric-looking label.
 */
export function isSymmetric(
  rt: Pick<RelationshipTypeInfo, 'key' | 'label' | 'inverse_label' | 'storage'>,
  from: string, to: string,
): boolean {
  /* EDGE only, because that is what the server says. `core.related_counts`
     collapses a pair to one `both` slot under
     `t.storage = 'edge' and p.from_type = p.to_type and …` (#2183), and this
     predicate is the host's half of that agreement. Without the storage test a
     self-referential FK with an equal label — `core_organization.parent_id`
     labelled "Same org as" on both ends — is ONE chip here and TWO directional
     slots there, so the counts beside the chip are somebody else's.

     `storage` is `not null check (storage in ('fk','edge'))` (core/0001), a
     closed vocabulary of two, so `=== 'edge'` and the `!== 'fk'` normalisation
     a few lines below cannot disagree on a real row. The server's spelling is
     used here on purpose: when the two halves of an agreement are written in
     different words, the next person has to prove they still mean the same
     thing. */
  if (rt.storage !== 'edge') return false
  if (from !== to) return false
  const inv = rt.inverse_label?.trim()
  return !!inv && inv === rt.label?.trim()
}

/**
 * Every endpoint pair a relationship type accepts.
 *
 * For an EDGE, `core.relationship_type_pair` is the authority, not the type's
 * own from_type/to_type: the insert trigger (core 0025) validates against the
 * pair table and raises "(x, y) is not a registered endpoint pair" for anything
 * else. A trigger seeds the canonical pair on insert and 0025 backfilled the
 * existing ones, so the pair set normally CONTAINS the type's own — but where
 * they disagree (a later UPDATE of from_type/to_type does not re-seed) the
 * database will refuse what the type row claims. Offering a link the write
 * would reject is worse than not offering it, so the pairs win when present.
 *
 * Since core 0.249.0 (#1435) an FK reads the pair table too. It used to return
 * the type's own from/to and nothing else — which was the CLIENT half of the
 * gap that issue is about: a column pointing at "some entity" could not be
 * declared, so the second target's related list did not exist. The pair table
 * is the platform's one polymorphism declaration and now both storages read it;
 * `core.get_neighbours` and `core.related_counts` made the same move in the
 * same release, so there is one answer rather than two.
 *
 * `value` is the token in the type's `discriminator_column` that selects a
 * pair, defaulting to the pair's own `to` — the server defaults it identically.
 * It is undefined when the type declares no discriminator, and every consumer
 * then behaves exactly as it did before.
 *
 * Since core 0.256.0 (#1455 / ADR F-33.1) `to_type` can be NULL: a
 * TARGET-NEUTRAL declaration, where the source module owns the pointer and each
 * module that introduces a real target owns one pair row. The declared pairs
 * are then the ONLY statement of what the column may point at, so the pre-0025
 * fallback below is available only while there is a canonical target to fall
 * back TO. A null target with no pairs yields NO slot — never `String(null)`,
 * never an empty label, never a guessed entity. The list simply is not there
 * yet, which is the truth: no bridge has declared a target.
 */
export function endpointPairs(
  rt: RelationshipTypeInfo,
  pairs: readonly RelationshipPairInfo[],
): { from: string; to: string; value?: string }[] {
  const own = rt.to_type == null ? null : { from: rt.from_type, to: rt.to_type }
  const declared = pairs.filter((p) => p.rel_type === rt.key)
  // pre-0025 payload — the type is all there is, IF it names a target at all
  if (declared.length === 0) return own ? [own] : []
  const disc = rt.discriminator_column ?? null
  // FAIL CLOSED. An fk with two declared targets and no discriminator is a
  // declaration the surface cannot honour: there is nothing to filter on, so
  // both slots would read each other's rows. The fleet ratchet R2
  // (`polymorphic-declaration.test.ts`) makes that state impossible, and this
  // is what the console does if one ever reaches it — show the canonical pair
  // rather than two lists that are quietly wrong, and show nothing at all when
  // there is no canonical pair to retreat to.
  if (rt.storage === 'fk' && !disc && declared.length > 1) return own ? [own] : []
  const out = new Map<string, { from: string; to: string; value?: string }>()
  for (const p of declared) {
    out.set(`${p.from_type}>${p.to_type}`, {
      from: p.from_type,
      to: p.to_type,
      ...(disc ? { value: p.discriminator_value ?? p.to_type } : {}),
    })
  }
  return [...out.values()]
}

/**
 * What a slot may truthfully OFFER to write (#2849, ROOT c.116633).
 *
 * `describe_schema` answers a GENERIC relationship with create + link (and
 * update + end for an edge) whatever the far entity's write door (core
 * 2026091522:394-398). For a far entity written through its module's own door
 * (`write_door = 'rpc'`) core refuses two of those: a generic create is an
 * invoker insert into that entity's table, and a generic FK link updates it
 * and refuses a non-direct door (2026091522:220-223). An EDGE link writes only
 * `core.relationship`, so it stays. A governed contract names its own
 * operations and is left exactly as the server said.
 *
 * DIRECTION (ROOT c.116770 G-W): a generic FK link is `update <from_type> set
 * <fk_column> = self`, so it writes the FAR row only from the TO end ('in').
 * From the FROM end the FK is this record's own field, which generic link
 * refuses for that reason whatever either door is — not this correction's to
 * withhold, so it is left as the server said (and is never a list Add).
 */
function offeredWriteOperations(
  rt: Pick<RelationshipTypeInfo, 'write_mode' | 'write_operations'>,
  storage: 'edge' | 'fk',
  direction: RelationSlot['direction'],
  farDoor: WriteDoor,
): RelationSlot['writeOperations'] {
  if (rt.write_mode !== 'generic' || farDoor !== 'rpc') return rt.write_operations
  const writesFarRow = storage === 'fk' && direction === 'in'
  return rt.write_operations?.filter((op) => op !== 'create' && !(writesFarRow && op === 'link'))
}

/**
 * The slots for one entity: every registered way a record of `entity` can be
 * related to something else, in both directions.
 *
 * A self-referencing relationship (org `parent_of` org) yields TWO slots — the
 * parents and the children — which is why the id carries the direction. Without
 * that, "Parent of" and "Subsidiary of" would collapse into one list showing
 * half the graph, which is precisely the bug `PartyRelations` avoided by
 * hardcoding both readings.
 *
 * Ordering is stable and meaningful rather than alphabetical: lists before
 * single-value fields (a record page is mostly about its children), identity
 * edges last (a merge candidate is not day-to-day work), then by label.
 */
export function deriveRelationSlots(schema: SchemaDescription, entity: string): RelationSlot[] {
  const labelOf = new Map((schema.entities ?? []).map((e: EntityTypeInfo) => [e.key, e.label]))
  const known = new Set((schema.entities ?? []).map((e) => e.key))
  const pairs = schema.relationship_pairs ?? []
  const slots = new Map<string, RelationSlot>()

  for (const rt of schema.relationships ?? []) {
    if (!rt?.key) continue
    const storage: 'edge' | 'fk' = rt.storage === 'fk' ? 'fk' : 'edge'
    for (const pair of endpointPairs(rt, pairs)) {
      // A SYMMETRIC self-referential edge is ONE reading, not two. Emitting a
      // slot per direction gave two chips with the same word — and, far worse
      // than the duplicate label, each one queried only its own end
      // (`from_id = me` / `to_id = me`), so a record's partners were split
      // across them by the accident of who created the link and NEITHER chip
      // showed them all.
      const directions = isSymmetric(rt, pair.from, pair.to)
        ? (['both'] as const)
        : (['out', 'in'] as const)
      for (const direction of directions) {
        const self = direction === 'in' ? pair.to : pair.from
        if (self !== entity) continue
        const otherEntity = direction === 'in' ? pair.from : pair.to
        // An endpoint the metamodel does not know is not renderable: it has no
        // table to read and no label to show. Skipping beats a dead tab.
        if (!known.has(otherEntity)) continue
        // The from-end of a foreign key is a column on THIS record — one value,
        // set on the record, not a list to append to.
        const kind: 'list' | 'field' = storage === 'fk' && direction === 'out' ? 'field' : 'list'
        const id = `${rt.key}:${direction}:${otherEntity}`
        if (slots.has(id)) continue
        slots.set(id, {
          id,
          relType: rt.key,
          label: direction === 'out' ? rt.label : inboundLabel(rt),
          direction,
          selfEntity: entity,
          otherEntity,
          otherLabel: labelOf.get(otherEntity) ?? otherEntity,
          storage,
          fkColumn: rt.fk_column ?? null,
          discriminatorColumn: storage === 'fk' ? rt.discriminator_column ?? null : null,
          discriminatorValue: storage === 'fk' ? pair.value ?? null : null,
          cardinality: rt.cardinality,
          kind,
          isIdentity: Boolean(rt.is_identity),
          module: rt.module,
          attributes: storage === 'edge' ? relationshipAttributes(rt.attributes) : [],
          writeMode: rt.write_mode,
          writeOperations: offeredWriteOperations(rt, storage, direction, writeDoorOf(schema, otherEntity)),
        })
      }
    }
  }

  // A POLYMORPHIC relationship produces one slot per endpoint entity — an
  // agreement's parties are organizations AND people — and they would otherwise
  // all be called "Parties". The relationship names what the link MEANS and the
  // entity says which kind, but only where that is actually ambiguous: adding
  // "· People" to a slot that has no sibling is noise.
  const byReading = new Map<string, RelationSlot[]>()
  for (const slot of slots.values()) {
    const k = `${slot.relType}:${slot.direction}`
    byReading.set(k, [...(byReading.get(k) ?? []), slot])
  }
  for (const group of byReading.values()) {
    if (group.length < 2) continue
    for (const slot of group) slot.label = `${slot.label} · ${slot.otherLabel}`
  }

  const rank = (s: RelationSlot) => (s.isIdentity ? 2 : s.kind === 'list' ? 0 : 1)
  return [...slots.values()].sort((a, b) =>
    rank(a) - rank(b) || a.label.localeCompare(b.label) || a.id.localeCompare(b.id))
}

/**
 * Resolve the relationship type's attribute object into the same deterministic
 * order a form would use. The declaration is data: unknown keys are rendered,
 * and absent declarations render no client-invented fields.
 */
export function relationshipAttributes(
  declared: Record<string, RelationshipAttributeInfo> | undefined,
): RelationAttribute[] {
  return Object.entries(declared ?? {})
    .map(([key, a]) => ({
      key,
      label: a?.label?.trim() || humanize(key),
      kind: a?.kind ?? 'text',
      options: Array.isArray(a?.options) ? a.options : [],
      sort: Number.isFinite(a?.sort) ? Number(a.sort) : Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label) || a.key.localeCompare(b.key))
    .map(({ sort: _sort, ...a }) => a)
}

/**
 * How to READ the rows on the other end of a slot, expressed as a filter over
 * the linked table — so a caller does one select and never guesses a shape.
 *
 * `edge` slots read `core.relationship` (the caller then resolves the ids);
 * `fk` slots read the OTHER entity's own table, filtered on the column that
 * points back here. Returns null for a `field` slot: that value is already on
 * the record the page has loaded, and re-fetching it would be a second, slower
 * source of truth for something already in hand.
 */
export type RelationRead =
  | { via: 'edge'; where: Record<string, string>; /** which end of the edge the OTHER row is on */ otherEnd: 'from_id' | 'to_id' }
  /**
   * A SYMMETRIC self-referential edge, which lives on BOTH ends. Two reads
   * rather than one `or=` filter: `where` is an equality map by design ("the
   * shape a caller applies — it never invents SQL"), and widening it to carry
   * disjunctions would change that contract for every caller to serve one case.
   * The caller runs both and merges.
   */
  | { via: 'edge-both'; ends: [EdgeEnd, EdgeEnd] }
  | { via: 'fk'; entity: string; where: Record<string, string> }

interface EdgeEnd { where: Record<string, string>; otherEnd: 'from_id' | 'to_id' }

export function relationRead(slot: RelationSlot, recordId: string): RelationRead | null {
  if (slot.kind === 'field') return null
  if (slot.storage === 'edge') {
    if (slot.direction === 'both') {
      return {
        via: 'edge-both',
        ends: [
          { where: { rel_type: slot.relType, from_id: recordId, from_type: slot.selfEntity, to_type: slot.otherEntity }, otherEnd: 'to_id' },
          { where: { rel_type: slot.relType, to_id: recordId, to_type: slot.selfEntity, from_type: slot.otherEntity }, otherEnd: 'from_id' },
        ],
      }
    }
    /* THIS record's type travels in the filter too (#2000 audit).
       It is a true predicate — an out-edge's `from_type` IS the entity the slot
       was derived for — so the server answers the same rows. What it buys is
       IDENTITY: without it the cached key says which relationship and which far
       entity but never which KIND of record the id belongs to, so nothing could
       tell this record's edges from a different entity's record sharing the
       uuid. A reconciliation that cannot tell them apart has to reconcile
       both. */
    return slot.direction === 'out'
      ? { via: 'edge', where: { rel_type: slot.relType, from_id: recordId, from_type: slot.selfEntity, to_type: slot.otherEntity }, otherEnd: 'to_id' }
      : { via: 'edge', where: { rel_type: slot.relType, to_id: recordId, to_type: slot.selfEntity, from_type: slot.otherEntity }, otherEnd: 'from_id' }
  }
  // fk + inbound: the other entity's rows carry the column pointing at us — and
  // on a DISCRIMINATED fk, the type column beside it says which of the declared
  // targets each row meant. Filtering on the id alone is right only while ids
  // happen not to collide across the target tables, and nothing enforces that:
  // a polymorphic pointer has no foreign key (#1435).
  return slot.fkColumn
    ? {
      via: 'fk',
      entity: slot.otherEntity,
      where: {
        [slot.fkColumn]: recordId,
        ...(slot.discriminatorColumn && slot.discriminatorValue
          ? { [slot.discriminatorColumn]: slot.discriminatorValue }
          : {}),
      },
    }
    : null
}

/**
 * The palette verb for a slot — ONE definition, used by both record surfaces.
 *
 * It names the DESTINATION, in the same words as the tab it lands on, so the
 * command list and the record read as one surface. Naming the entity instead is
 * both ambiguous and wrong-sounding: an organization's two `parent_of` slots
 * would each be "add organization" (a coin toss), and registered entity labels
 * are PLURAL nav labels, so it comes out "add people".
 *
 * `field` slots never reach here: they are edited on the form, and offering
 * "add" for a column holding one value would misdescribe what the click does.
 */
export function relationVerbLabel(slot: RelationSlot): string {
  return `Add to ${slot.label}`
}

/** A missing capability is closed, never the old raw-write behavior. */
export function relationshipWriteOffered(
  slot: Pick<RelationSlot, 'writeOperations'>,
  operation: 'create' | 'link' | 'update' | 'end',
): boolean {
  return slot.writeOperations?.includes(operation) === true
}

/**
 * May a person ADD to this slot — create a related record or link an existing
 * one (#2849)? ONE answer for every Add a record offers: the plate's heading
 * Add and empty row, the Levels card, and the record palette's "Add to …"
 * verb. `deriveRelationSlots` has already narrowed the offer to what core
 * admits for the written entity's door.
 */
export function slotOffersAdd(slot: Pick<RelationSlot, 'writeOperations'>): boolean {
  return relationshipWriteOffered(slot, 'create') || relationshipWriteOffered(slot, 'link')
}

/** The slots a record surface shows as lists — `field` slots live on the form. */
export function listSlots(schema: SchemaDescription | undefined, entity: string): RelationSlot[] {
  return schema ? deriveRelationSlots(schema, entity).filter((slot) => slot.kind === 'list') : []
}

/**
 * The record's palette scope — its verbs ARE its relationships.
 *
 * Replaces `RecordPage`'s hardcoded filter (`sec.related === 'contacts' ||
 * 'opportunities' || 'companies'`) and the `entity === 'core_organization'`
 * branch beside it. `openSlot` is what a verb does: bring that related list into
 * view so the user can act on it — which the cascade honours by expanding the
 * section, the tab strip having been retired (2026-08-07). A slot whose Add the
 * plate withholds offers no verb either: running one opened the same add flow
 * into a refusal (#2849).
 */
export function recordPaletteScope(
  entity: string, id: string, label: string, slots: RelationSlot[],
  openSlot: (slotId: string) => void,
  openRecord: (recordId: string) => void,
): PaletteScope {
  return {
    entity,
    label,
    recordId: id,
    verbs: slots.filter(slotOffersAdd).map((slot) => ({
      label: relationVerbLabel(slot),
      run: () => openSlot(slot.id),
    })),
    open: openRecord,
  }
}
